import React from 'react';
import AudioPlayer from './AudioPlayer';
import './Mysterion.css';

const Mysterion = () => {
  return (
    <div className="mysterion-page" id="top">
      <div className="header-image-container">
        <img 
          src="/images/mysterion.png" 
          alt="Mysterion" 
          className="header-image"
        />
        <div className="header-overlay">
          <p className="page-subtitle">Unveiling the Sacred Secrets of God</p>
        </div>
      </div>
      
      {/* Mysterion Introduction Section */}
      <section className="content-section mysterion-intro-section" id="mysterion">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              {/* Audio Player */}
              <AudioPlayer 
                audioSrc="/audio/Mysterion-Page-Podcast.mp3" 
                title="Mysterion Podcast Episode"
                description="Dive deeper with our audio discussion on mysticism and divine mysteries"
              />
              
              <p>
                Welcome to Mysterion. In the New Testament, the Greek word μυστήριον (mysterion) does not mean a riddle we must solve, but rather a profound truth that was once hidden and is now revealed by God to His people. It is a divine secret made known through the revelation of Jesus Christ and the work of the Holy Spirit. This "secret" consists of many interactions, all pointing to the Revelation of Jesus Christ as the Redeemer and Savior, concealed until the fullness of time.
              </p>
              <p>
                The Apostle Paul speaks of this often. He describes the Gospel as "the mystery hidden for ages and generations but now revealed to his saints" (Colossians 1:26). This revelation is not meant for a select few, but for all who are in Christ. It is the unveiling of God's magnificent plan for redemption and His desire for intimate fellowship with humanity.
              </p>
              <p>
                This section of our website is dedicated to exploring the beautiful and deep mysteries of our faith. We invite you to journey with us as we delve into:
              </p>
              <p>
                <strong>Mysteries and Secrets in the Bible:</strong> Discover the specific "mysterion" concepts the apostles wrote about, from the nature of the Church to the hope of glory dwelling within us.
              </p>
              <p>
                <strong>Christian Mysticism:</strong> Understand the historical and biblical pursuit of a direct, experiential knowledge of God. This is not about strange rituals, but about the heart's deep longing for union with its Creator. In fact, a common phrase used by the mystical community is "mystical Union"
              </p>
              <p>
                <strong>Christian Mystics:</strong> Learn from the lives and writings of believers throughout history who walked this path of deep communion, leaving a legacy of wisdom and devotion for us to follow.
              </p>
              <p>
                Our prayer is that as you explore these pages, your heart will be stirred to seek the deeper things of God, who "rewards those who earnestly seek him" (Hebrews 11:6).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mysteries Section */}
      <section className="content-section mysteries-section" id="mysteries">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <h2 className="section-title">Mysteries and Secrets in the Bible</h2>
              <p className="section-subtitle">The Revealed Mysteries of Scripture</p>
              <p>
                The Bible is not a book of unsolved puzzles. Instead, it is the story of God revealing Himself and His divine plan. When the New Testament uses the word "mystery" (μυστήριον), it refers to a specific aspect of this plan that was previously concealed but is now made clear in Christ. Here are some of the central mysteries revealed in the pages of Scripture:
              </p>
              
              <h3>1. The Mystery of the Kingdom of God</h3>
              <p>
                <strong>Scripture:</strong> "To you has been given the secret of the kingdom of God, but for those outside everything is in parables" (Mark 4:11).
              </p>
              <p>
                <strong>Explanation:</strong> Jesus revealed that the Kingdom is not merely a future destination but a present reality that is entered into through faith in Him. It is a spiritual realm governed by God's will, accessible here and now.
              </p>
              
              <h3>2. The Mystery of Christ in You</h3>
              <p>
                <strong>Scripture:</strong> "The mystery hidden for ages and generations but now revealed to his saints. To them God chose to make known how great among the Gentiles are the riches of the glory of this mystery, which is Christ in you, the hope of glory" (Colossians 1:26-27).
              </p>
              <p>
                <strong>Explanation:</strong> This is the core of the New Covenant reality. The living Christ, through the Holy Spirit, literally takes up residence in the heart of every believer, becoming our source of life and our certain hope for future glory.
              </p>
              
              <h3>3. The Mystery of Christ and the Church</h3>
              <p>
                <strong>Scripture:</strong> "'Therefore a man shall leave his father and mother and hold fast to his wife, and the two shall become one flesh.' This mystery is profound, and I am saying that it refers to Christ and the church" (Ephesians 5:31-32).
              </p>
              <p>
                <strong>Explanation:</strong> The union between a husband and wife is a beautiful picture of a deeper, spiritual reality: the inseparable union between Christ (the Bridegroom) and the Church (His Bride).
              </p>
              
              <h3>4. The Mystery of the Gospel</h3>
              <p>
                <strong>Scripture:</strong> "...that words may be given to me in opening my mouth boldly to proclaim the mystery of the gospel" (Ephesians 6:19).
              </p>
              <p>
                <strong>Explanation:</strong> The good news itself was a mystery—that God would save both Jews and Gentiles through faith in the crucified and risen Messiah, creating one new family in Him.
              </p>
              
              <h3>5. The Mystery of the Resurrection</h3>
              <p>
                <strong>Scripture:</strong> "Behold! I tell you a mystery. We shall not all sleep, but we shall all be changed, in a moment, in the twinkling of an eye, at the last trumpet" (1 Corinthians 15:51-52).
              </p>
              <p>
                <strong>Explanation:</strong> Paul unveils the future event where believers, both living and dead, will receive glorified, imperishable bodies.
              </p>
              
              <p>
                These revealed mysteries are not topics for mere academic study; they are foundational truths meant to be lived, experienced, and worshiped.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mysticism Section */}
      <section className="content-section mysticism-section" id="mysticism">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <h2 className="section-title">Christian Mysticism: The Pursuit of Divine Encounter</h2>
              <p>
                What is Christian mysticism? The term can be misunderstood, but at its heart, Christian mysticism is the practice and experience of seeking a direct, intimate, and transformative union with God.
              </p>
              <p>
                It is not about seeking secret knowledge outside of Scripture (Gnosticism) or emptying the mind into a cosmic void. Rather, it is the fulfillment of the prayer of Jesus Himself: "that they may all be one, just as you, Father, are in me, and I in you, that they also may be in us" (John 17:21). It is built on the revealed "mysterion" of "Christ in you, the hope of glory."
              </p>
              
              <h3>Core Principles of Christian Mysticism:</h3>
              <p>
                <strong>It is Christ-Centered:</strong> The goal is not a vague spiritual experience, but a deeper communion with the person of Jesus Christ. It is a response to His invitation: "Abide in me, and I in you" (John 15:4).
              </p>
              <p>
                <strong>It is Rooted in Scripture:</strong> The experiences of the mystics are tested and understood through the lens of the Bible. Psalm 46:10, "Be still, and know that I am God," is a foundational verse for this path.
              </p>
              <p>
                <strong>It Involves Transformation:</strong> A true encounter with God always leads to transformation. The goal is what the Apostle Paul describes in Galatians 2:20: "It is no longer I who live, but Christ who lives in me." This is a process of yielding our will to His and being conformed to His image.
              </p>
              
              <h3>The Threefold Way (Via Triplex):</h3>
              <p>
                Historically, the mystical journey has often been described in three stages:
              </p>
              <p>
                <strong>Purgation (The Way of Purification):</strong> This is the initial stage of repentance, turning from sin, and actively purifying one's heart and intentions through spiritual disciplines.
              </p>
              <p>
                <strong>Illumination (The Way of Enlightenment):</strong> As the soul is cleansed, it becomes more receptive to the light of God. This stage is marked by a deeper understanding of spiritual truths and a growing awareness of God's presence.
              </p>
              <p>
                <strong>Union (The Way of Union):</strong> This is the goal of the mystical path—a state of deep, abiding communion with God, where the soul rests in His love and experiences a profound oneness with Him.
              </p>
              
              <p>
                Christian mysticism is simply the art of cultivating a deep, personal relationship with God, moving beyond knowing about Him to knowing Him experientially.
              </p>
              
              <p className="author-note">
                <strong>Authors note:</strong> Throughout history, many students of scripture have, in good faith, interpreted the bible in many different ways. It is my personal opinion that there are two main reasons for this.
              </p>
              <p className="author-note">
                First, the way scripture is learned is by practising due diligence in studying the scripture. The second is by listening to the Holy Spirit (our teacher) as He explains what He wants us to know as we study.
              </p>
              <p className="author-note">
                Secondly, the way we study the scripture within the bounds of integrity and being led by Holy Spirit can lead scholars to interpret the scripture in different ways, according to what the Holy Spirit is wanting to emphasize.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mystics Section */}
      <section className="content-section mystics-section" id="mystics">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <h2 className="section-title">Christian Mystics: Witnesses to the Deeper Walk</h2>
              <p>
                Throughout church history, God has raised up men and women who pursued Him with extraordinary passion and documented their journey. These individuals, often called "mystics," are not a special class of super-saints, but brothers and sisters in faith whose lives and writings serve as powerful testimonies to the reality of a deep, experiential relationship with God.
              </p>
              <p>
                They are witnesses who can encourage and guide us in our own pursuit of Him. Here are a few notable figures from this great cloud of witnesses:
              </p>
              
              <h3>Hildegard of Bingen (1098–1179)</h3>
              <p>
                A German Benedictine abbess, writer, composer, and visionary. Hildegard's work explores the integration of faith, creation, and divine revelation. She described her powerful visions of the "living light" and wrote extensively on theology and natural history.
              </p>
              
              <h3>Julian of Norwich (c. 1343–c. 1416)</h3>
              <p>
                An English anchoress (a type of religious hermit) who lived through a time of great turmoil. After receiving a series of sixteen revelations from Christ, she wrote Revelations of Divine Love, a work of profound comfort and theological depth, famous for the phrase, "All shall be well, and all shall be well, and all manner of thing shall be well."
              </p>
              
              <h3>Teresa of Ávila (1515–1582)</h3>
              <p>
                A Spanish Carmelite nun and a Doctor of the Church. Her most famous work, The Interior Castle, uses the metaphor of a crystal castle with many mansions to describe the journey of the soul towards union with God at its center.
              </p>
              
              <h3>John of the Cross (1542–1591)</h3>
              <p>
                A contemporary and friend of Teresa of Ávila, John is known for his profound poetry and writings on the soul's journey. He is most famous for the concept of the "Dark Night of the Soul," describing a period of spiritual desolation that ultimately serves to purify the soul and lead it to a deeper union with God.
              </p>
              
              <h3>Brother Lawrence (c. 1614–1691)</h3>
              <p>
                A humble lay brother in a Carmelite monastery in Paris. He is beloved for his simple yet profound path to communion with God. His letters and conversations were compiled into the classic book, The Practice of the Presence of God, which teaches that one can be in constant fellowship with God through every mundane task of the day.
              </p>
              
              <h3>A.W. Tozer (1897–1963)</h3>
              <p>
                A 20th-century American pastor and writer, often called a "modern-day prophet." Though he might not have used the term "mystic" for himself, his passionate writings, especially in The Pursuit of God, champion the necessity of moving beyond intellectual assent to a real, experiential knowledge of the living God.
              </p>
              
              <p>
                The lives of these mystics remind us that the invitation to "draw near to God" is real, and that He is a rewarder of those who diligently seek Him (James 4:8, Hebrews 11:6).
              </p>
              
              <div className="conclusion-paragraph">
                <p>
                  As you conclude your exploration of "Mysterion: Unveiling the Sacred Secrets of God," we hope you've gained clarity on the New Testament concept of mysterion as a profound truth once hidden but now revealed by God through Jesus Christ.
                </p>
                <p>
                  This page, through its sections on Biblical mysteries, Christian mysticism, and Christian mystics, aims to equip you to be more aware of mysticism's role throughout church history. May these insights encourage you to seek the deeper things of God and pursue an earnest, experiential relationship with Him.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Anchors */}
      <div className="section-navigation">
        <div className="container">
          <div className="nav-links">
            <a href="#top" className="nav-link">Mysterion</a>
            <a href="#mysteries" className="nav-link">Mysteries</a>
            <a href="#mysticism" className="nav-link">Mysticism</a>
            <a href="#mystics" className="nav-link">Mystics</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Mysterion;